// The captured document itself.
//
// A digest you can't click into is a list of assertions. This is the
// panel that answers, for any single item: what was actually scraped,
// what URL it came from, when the source published it, when we fetched
// it, which lane found it, and where the bytes live in R2.
//
// The content is shown verbatim — no re-rendering of the markdown into
// HTML — because these exact bytes are what Phase 3's claim binding
// will match spans against. Seeing them as the pipeline stored them is
// the point.

import { useEffect, useState } from 'react';
import { ExternalLink, X, Copy, Check } from 'lucide-react';
import { pipelineApi } from '../lib/api';
import type { SnapshotDetail } from '@shared/types';

function fmt(ms: number | null): string {
  if (ms === null) return '—';
  return new Date(ms).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function relative(ms: number | null): string {
  if (ms === null) return '';
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export default function SnapshotDrawer({
  snapshotId,
  onClose,
}: {
  snapshotId: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<SnapshotDetail | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setContent(null);
    setErr(null);
    void (async () => {
      try {
        const res = await pipelineApi.snapshot(snapshotId);
        if (cancelled) return;
        setDetail(res.snapshot);
        setContent(res.content);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed to load snapshot');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [snapshotId]);

  // Escape closes, because a panel you can't dismiss with the keyboard
  // is a panel you stop opening.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-slate-900/30" onClick={onClose} aria-hidden />
      <aside className="w-full sm:w-[46rem] max-w-full bg-white border-l border-slate-200 shadow-xl flex flex-col">
        <header className="flex items-start gap-3 px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-medium text-slate-900 leading-snug">
              {detail?.title || detail?.url || 'Loading…'}
            </h2>
            {detail && (
              <a
                href={detail.url}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:underline break-all"
              >
                {detail.url}
                <ExternalLink size={11} className="flex-shrink-0" />
              </a>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost flex-shrink-0" title="Close (Esc)">
            <X size={16} />
          </button>
        </header>

        {err && <p className="px-5 py-4 text-sm text-red-600">{err}</p>}

        {detail && (
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex-shrink-0">
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
              <Field label="Source" value={detail.source_name} mono={false} />
              <Field
                label="Published"
                value={`${fmt(detail.published_at)}${
                  detail.published_at ? ` · ${relative(detail.published_at)}` : ''
                }`}
              />
              <Field label="Scraped" value={`${fmt(detail.fetched_at)} · ${relative(detail.fetched_at)}`} />
              <Field label="Found by" value={detail.lane === 'search' ? 'search lane' : 'feed lane'} />
              <Field label="Via" value={detail.discovered_via ?? '—'} />
              <Field label="Size" value={`${detail.byte_len.toLocaleString()} bytes`} />
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-slate-500">Stored at</dt>
                <dd className="font-mono text-[11px] text-slate-700 break-all">
                  {detail.r2_key}
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-slate-500">Content hash</dt>
                <dd className="font-mono text-[11px] text-slate-700 break-all">
                  {detail.content_hash}
                </dd>
              </div>
            </dl>
          </div>
        )}

        <div className="flex items-center justify-between px-5 py-2 border-b border-slate-100 flex-shrink-0">
          <span className="text-xs text-slate-500">Captured text, exactly as stored</span>
          {content && (
            <button
              className="btn-ghost text-xs"
              onClick={() => {
                void navigator.clipboard.writeText(content);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {content === null && !err ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : content === null ? null : content.trim().length === 0 ? (
            <p className="text-sm text-slate-400">
              The stored object is empty. That is a capture bug, not an empty page — the
              pipeline only stores records that had text.
            </p>
          ) : (
            <pre className="text-xs leading-relaxed text-slate-800 whitespace-pre-wrap font-mono">
              {content}
            </pre>
          )}
        </div>
      </aside>
    </div>
  );
}

function Field({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`${mono ? 'font-mono text-[11px]' : ''} text-slate-800 truncate`} title={value}>
        {value}
      </dd>
    </div>
  );
}
